import React, { useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useGetParticipantListsQuery } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { rejectFailedParticipantListSave, useCreateParticipantListMutation } from 'src/api/v1/contact-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { ParticipantListIcon } from 'src/icons/ParticipantListIcon';
import { ParticipantListCard } from 'src/pages/ProfileSettingsPage/components/ParticipantListCard';
import { ParticipantListNameInput } from 'src/pages/ProfileSettingsPage/components/ParticipantListNameInput';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { getV1ErrorMessage } from 'src/utils/v1ErrorMessage';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

enum EditorKind {
    NEW_LIST = 'newList',
    RENAME = 'rename',
    ADD_PARTICIPANT = 'addParticipant',
}

// only one inline editor is open at a time, like on the legacy page
type Editor =
    | { kind: EditorKind.NEW_LIST }
    | { kind: EditorKind.RENAME, listId: number }
    | { kind: EditorKind.ADD_PARTICIPANT, listId: number }
    | null;

export const ParticipantListsSection = () => {
    const dispatch = useAppDispatch();
    const { data } = useGetParticipantListsQuery();
    const [createParticipantList, { isLoading: isCreateLoading }] = useCreateParticipantListMutation();
    const [openListId, setOpenListId] = useState<number | null>(null);
    const [editor, setEditor] = useState<Editor>(null);

    const lists = data?.lists ?? [];
    const roles = data?.roles ?? [];

    const showError = (text: string) => {
        dispatch(showSnackBar({ text, severity: 'error' }));
    };

    const isNameTaken = (name: string, exceptListId?: number) =>
        lists.some(list => list.id !== exceptListId && list.name.trim() === name.trim());

    const toggleList = (listId: number) => {
        setEditor(null);
        setOpenListId(current => current === listId ? null : listId);
    };

    const handleCreate = (name: string) => {
        const trimmed = name.trim();
        if (trimmed === '') {
            showError(ts('pl_invalid_name_enter', 'Please enter a name for the participant list.'));
            return;
        }
        if (isNameTaken(trimmed)) {
            showError(ts('pl_existing_list_name', 'A participant list with that name already exists.'));
            return;
        }
        createParticipantList({ list_name: trimmed })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(response => {
                dispatch(showSnackBar({ text: response.message, severity: 'success' }));
                setEditor(null);
                setOpenListId(response.id ?? null);
            })
            .catch(error => showError(
                getV1ErrorMessage(error, ts('something_went_wrong_try_again', 'Something went wrong, please try again.'))
            ));
    };

    return (
        <SettingsSection
            title={ts('profile_settings_participant_lists', 'Participant lists')}
            titleIcon={<ParticipantListIcon />}
            titleAction={
                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_participant_lists_caption', 'Invite people to projects at once')}
                </Typography>
            }
        >
            {data && (
                <Stack spacing={1}>
                    {lists.map(list => (
                        <ParticipantListCard
                            key={list.id}
                            list={list}
                            roles={roles}
                            isOpen={openListId === list.id}
                            isRenaming={editor?.kind === EditorKind.RENAME && editor.listId === list.id}
                            isAddingParticipant={editor?.kind === EditorKind.ADD_PARTICIPANT && editor.listId === list.id}
                            isNameTaken={isNameTaken}
                            onToggle={() => toggleList(list.id)}
                            onStartRename={() => setEditor({ kind: EditorKind.RENAME, listId: list.id })}
                            onStartAddParticipant={() => setEditor({ kind: EditorKind.ADD_PARTICIPANT, listId: list.id })}
                            onCloseEditor={() => setEditor(null)}
                        />
                    ))}
                    {editor?.kind === EditorKind.NEW_LIST ? (
                        <ParticipantListNameInput
                            disabled={isCreateLoading}
                            confirmTooltip={ts('plt_add_new_list', 'Add new participant list')}
                            cancelTooltip={ts('plt_cancel_new_list', 'Cancel adding new list')}
                            onConfirm={handleCreate}
                            onCancel={() => setEditor(null)}
                        />
                    ) : (
                        <Box>
                            <Tooltip title={ts('plt_add_new_list', 'Add new participant list')}>
                                <IconButton
                                    color="primary"
                                    sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}` }}
                                    onClick={() => setEditor({ kind: EditorKind.NEW_LIST })}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Stack>
            )}
        </SettingsSection>
    );
};
